// src/components/ui/DateRangePicker.jsx
// Shared date picker used by Publisher, EcommerceDashboard, Dashboard, HapiGuard
// Features: presets, custom dates, today vs yesterday comparison toggle, dark mode

import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

// ── helpers ───────────────────────────────────────────────────────────────────
export function fmtDate(d) {
  if (!d) return '';
  try {
    let dt;
    if (typeof d === 'string') {
      // Already a YYYY-MM-DD string — return as-is without touching timezone
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      dt = new Date(d.includes('T') ? d : d + 'T00:00:00');
    } else {
      dt = d instanceof Date ? d : new Date(d);
    }
    if (isNaN(dt.getTime())) return '';
    // Use LOCAL date parts — never toISOString() which converts to UTC
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  } catch { return ''; }
}
function addDays(d, n) {
  // Parse to local midnight then add days — avoids DST drift
  let r;
  if (typeof d === 'string') {
    const [y, mo, day] = d.split('-').map(Number);
    r = new Date(y, mo - 1, day);
  } else {
    r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  r.setDate(r.getDate() + n);
  return r;
}

export function formatDateRangeLabel(start, end) {
  if (!start || !end) return 'Select dates';
  // Convert Date objects to strings if needed
  start = start instanceof Date ? fmtDate(start) : start;
  end   = end   instanceof Date ? fmtDate(end)   : end;
  if (!start || !end) return 'Select dates';
  const opts = { month: 'short', day: 'numeric' };
  const s = typeof start === 'string' ? start : fmtDate(start);
  const e = typeof end   === 'string' ? end   : fmtDate(end);
  if (s === e) return new Date(s + 'T00:00:00').toLocaleDateString('en', { ...opts, year: 'numeric' });
  return `${new Date(s + 'T00:00:00').toLocaleDateString('en', opts)} – ${new Date(e + 'T00:00:00').toLocaleDateString('en', { ...opts, year: 'numeric' })}`;
}

export function getPresetDates(preset) {
  // Build today from local parts — never use toISOString() which shifts to UTC
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayStr = fmtDate(todayLocal);

  switch (preset) {
    case 'today':     return { start: todayStr, end: todayStr };
    case 'yesterday': return { start: fmtDate(addDays(todayLocal, -1)), end: fmtDate(addDays(todayLocal, -1)) };
    case '7d':        return { start: fmtDate(addDays(todayLocal, -6)),  end: todayStr };
    case '30d':       return { start: fmtDate(addDays(todayLocal, -29)), end: todayStr };
    case '90d':       return { start: fmtDate(addDays(todayLocal, -89)), end: todayStr };
    case 'mtd':       return { start: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: todayStr };
    case 'ytd':       return { start: fmtDate(new Date(now.getFullYear(), 0, 1)), end: todayStr };
    default:          return { start: fmtDate(addDays(todayLocal, -29)), end: todayStr };
  }
}

export function getComparisonDates(start, end, comparePreset) {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end   + 'T00:00:00');
  const diff = Math.ceil((e - s) / 86400000) + 1;
  if (comparePreset === 'year') {
    const cs = new Date(s); cs.setFullYear(cs.getFullYear() - 1);
    const ce = new Date(e); ce.setFullYear(ce.getFullYear() - 1);
    return { start: fmtDate(cs), end: fmtDate(ce) };
  }
  // previous period
  const ce = addDays(s, -1);
  const cs = addDays(ce, -(diff - 1));
  return { start: fmtDate(cs), end: fmtDate(ce) };
}

const PRESETS = [
  { label: 'Today',      value: 'today'     },
  { label: 'Yesterday',  value: 'yesterday' },
  { label: 'Last 7d',   value: '7d'        },
  { label: 'Last 30d',  value: '30d'       },
  { label: 'Last 90d',  value: '90d'       },
  { label: 'Month to date', value: 'mtd'   },
  { label: 'Year to date',  value: 'ytd'   },
];

// ── Main component ────────────────────────────────────────────────────────────
// Props:
//   startDate, endDate      — current applied dates (YYYY-MM-DD strings)
//   onChange({ start, end, compareStart, compareEnd, preset, compareEnabled })
//   showComparison          — show the compare toggle (default true)
//   defaultPreset           — initial preset (default 'today')
//   align                   — 'right' | 'left' (default 'right')
export default function DateRangePicker({
  startDate,
  endDate,
  onChange,
  showComparison = true,
  defaultPreset  = 'today',
  align          = 'right',
}) {
  const [open,            setOpen]            = useState(false);
  const todayStr = fmtDate(new Date());
  const [pendingStart,    setPendingStart]    = useState(fmtDate(startDate) || todayStr);
  const [pendingEnd,      setPendingEnd]      = useState(fmtDate(endDate)   || todayStr);
  const [pendingPreset,   setPendingPreset]   = useState(defaultPreset);
  const [compareEnabled,  setCompareEnabled]  = useState(true);
  const [comparePreset,   setComparePreset]   = useState('previous');
  const ref = useRef(null);

  // Sync pending when parent updates
  useEffect(() => {
    const s = fmtDate(startDate);
    const e = fmtDate(endDate);
    if (s) setPendingStart(s);
    if (e) setPendingEnd(e);
  }, [startDate, endDate]);

  // Close on outside click
  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  function openPicker() {
    const todayS = fmtDate(new Date());
    setPendingStart(fmtDate(startDate) || todayS);
    setPendingEnd(fmtDate(endDate)     || todayS);
    setOpen(true);
  }

  function handlePreset(preset) {
    const { start, end } = getPresetDates(preset);
    setPendingStart(start);
    setPendingEnd(end);
    setPendingPreset(preset);
  }

  function handleCustomStart(val) {
    setPendingStart(val);
    setPendingPreset('custom');
  }

  function handleCustomEnd(val) {
    setPendingEnd(val);
    setPendingPreset('custom');
  }

  function apply() {
    const comp = compareEnabled && showComparison
      ? getComparisonDates(pendingStart, pendingEnd, comparePreset)
      : null;
    onChange({
      start:        pendingStart,
      end:          pendingEnd,
      preset:       pendingPreset,
      compareEnabled,
      comparePreset,
      compareStart: comp?.start || null,
      compareEnd:   comp?.end   || null,
    });
    setOpen(false);
  }

  const compRange = showComparison && compareEnabled
    ? getComparisonDates(pendingStart, pendingEnd, comparePreset)
    : null;

  const label = formatDateRangeLabel(startDate, endDate);

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        onClick={openPicker}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border
          border-border bg-card text-foreground
          hover:bg-muted transition-colors text-sm font-medium min-w-[200px] justify-between"
      >
        <span className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="truncate">{label}</span>
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`absolute top-full mt-2 z-50
          bg-card border border-border rounded-xl shadow-xl p-4
          left-0 right-0 sm:left-auto sm:right-0 sm:w-[320px]
          ${align === 'left' ? 'sm:left-0 sm:right-auto' : ''}
        `}>
          {/* Preset pills */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {PRESETS.map(p => (
              <button
                key={p.value}
                onClick={() => handlePreset(p.value)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors border ${
                  pendingPreset === p.value
                    ? 'bg-primary text-white border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom date inputs */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Start Date</label>
              <input
                type="date"
                value={pendingStart}
                onChange={e => handleCustomStart(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm
                  bg-background text-foreground
                  focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">End Date</label>
              <input
                type="date"
                value={pendingEnd}
                onChange={e => handleCustomEnd(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm
                  bg-background text-foreground
                  focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Comparison toggle */}
          {showComparison && (
            <div className="border-t border-border pt-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">Compare to</span>
                <button
                  onClick={() => setCompareEnabled(v => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors ${compareEnabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${compareEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
              {compareEnabled && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    {[
                      { value: 'previous', label: 'Previous Period' },
                      { value: 'year',     label: 'Previous Year'   },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setComparePreset(opt.value)}
                        className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                          comparePreset === opt.value
                            ? 'bg-primary text-white border-primary'
                            : 'bg-background text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {compRange && (
                    <p className="text-xs text-muted-foreground">
                      Comparing: {compRange.start} → {compRange.end}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium
                bg-background text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={apply}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold
                hover:bg-primary/90 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
